import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchShopJson, SHOP_IMAGE_BASE } from "../components/shopApi";
import { pruneWishlist, readWishlist, toggleWishlist } from "../components/wishlistStorage";
import "../index.css";
import SeoManager from "../SeoManager";

function formatPrice(value) {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function WishlistPage() {
  const [products, setProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setWishlistIds(readWishlist());

    fetchShopJson("/get_products.php")
      .then((data) => {
        const availableProducts = Array.isArray(data) ? data : [];
        const availableIds = availableProducts.map((product) => product.id);
        setWishlistIds(pruneWishlist(availableIds));
        setProducts(availableProducts);
      })
      .catch((error) => {
        console.error(error);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const wishlistProducts = useMemo(() => {
    const lookup = new Map(products.map((product) => [String(product.id), product]));
    return wishlistIds.map((id) => lookup.get(String(id))).filter(Boolean);
  }, [products, wishlistIds]);

  const handleRemove = (productId) => {
    setWishlistIds(toggleWishlist(productId));
  };

  return (
    <div className="content-page">
      <SeoManager
        title="My Wishlist"
        description="Saved gold jewelry pieces in your Asher Jewels PH wishlist."
        path="/wishlist"
        robots="noindex,nofollow"
      />
      <section className="content-hero">
        <p className="category-section__eyebrow">Saved Pieces</p>
        <h1>My Wishlist</h1>
        <p>
          Keep track of the designs you want to revisit. Your wishlist is saved in this browser and
          automatically removes items that are no longer in stock.
        </p>
      </section>

      {loading ? (
        <div className="content-card"><p>Loading wishlist...</p></div>
      ) : wishlistProducts.length === 0 ? (
        <div className="content-card content-empty">
          <h2>Your wishlist is empty</h2>
          <p>Browse the catalog and tap the heart icon on any product to save it here.</p>
          <Link to="/" className="content-link-button">Continue Shopping</Link>
        </div>
      ) : (
        <section className="wishlist-grid">
          {wishlistProducts.map((product) => (
            <article key={product.id} className="wishlist-card">
              <Link to={`/product/${product.id}`} className="wishlist-card__image">
                <img src={`${SHOP_IMAGE_BASE}${product.imageUrl}`} alt={product.name} />
              </Link>
              <div className="wishlist-card__body">
                {(product.brand || "").trim() && (
                  <p className="product-card__brand">{(product.brand || "").trim()}</p>
                )}
                <h2>{product.name}</h2>
                <p className="wishlist-card__price">PHP {formatPrice(product.pricePHP)}</p>
                <p className="wishlist-card__description">{product.description}</p>
                <div className="wishlist-card__actions">
                  <Link to={`/product/${product.id}`} className="content-link-button">
                    View Details
                  </Link>
                  <button
                    type="button"
                    className="wishlist-remove-button"
                    onClick={() => handleRemove(product.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
